import axios from 'axios';
import { buildScoresListUrl } from '../student/scoreListApi';
import {
  SCORES_FETCH_TIMEOUT_MS,
  parseScoresListResponse,
} from '../student/scoreListMessages';

export const DASHBOARD_SUMMARY_TIMEOUT_MS = 10000;


export function buildDashboardCountsCacheKey(userId) {
  return `dashboard:counts:${userId}`;
}

export function buildDashboardResultsCacheKey(userId) {
  return `dashboard:results:${userId}`;
}

export function buildDashboardSummaryCacheKey(userId) {
  return `dashboard:summary:${userId}`;
}

/** Builds the aggregated dashboard summary URL. */
export function buildDashboardSummaryUrl(apiUrl, userId, { activityLimit = 50 } = {}) {
  const params = new URLSearchParams();
  if (activityLimit) params.set('activityLimit', String(activityLimit));
  const qs = params.toString();
  return `${apiUrl}/api/dashboard/summary/${userId}${qs ? `?${qs}` : ''}`;
}

/** Converts API trend rows to StackBars chart data ({ m, v }). */
export function mapDashboardTrend(trend) {
  if (!Array.isArray(trend)) return [];
  return trend.map((row) => ({
    m: row.month,
    v: [Number(row.submissions) || 0, Number(row.passed) || 0],
  }));
}

/**
 * Maps summary activity rows into the dashboard feed shape.
 * relTimeFn converts epoch ms to a relative time label.
 */
export function mapDashboardActivity(recentActivity, relTimeFn, colors = []) {
  if (!Array.isArray(recentActivity)) return [];
  return recentActivity.map((row, index) => ({
    who: row.who || 'Learner',
    role: row.role || 'Student',
    action: row.action || 'submitted',
    target: row.target || 'an assessment',
    course: row.course || '',
    time: relTimeFn(row.createdAt),
    createdAt: Number(row.createdAt) || 0,
    color: colors[index % colors.length],
    score: row.scorePct != null ? row.scorePct : null,
  }));
}

/** Parses assessment list payloads (array or paginated content). */
export function parseAssessmentList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
}

/** Resolves assessment count from list or paginated totalElements. */
export function resolveAssessmentCount(payload, list) {
  if (Array.isArray(payload)) return payload.length;
  return Number(payload?.totalElements ?? list.length);
}

/**
 * Normalizes score field names between testResult and studentcourses/results.
 */
export function normalizeDashboardResultRow(row) {
  if (!row || typeof row !== 'object') return row;
  const score = row.score ?? row.marksScored;
  return {
    ...row,
    score,
    totalMarks: row.totalMarks,
    createdAt: row.createdAt,
    firstName: row.firstName,
    lastName: row.lastName,
    studentEmail: row.studentEmail ?? row.email,
  };
}

/** Groups org-wide result rows by testId for dashboard charts and activity. */
export function groupResultsByTestId(rows) {
  const map = {};
  if (!Array.isArray(rows)) return map;

  rows.forEach((row) => {
    const id = row.testId ?? row.test_id;
    if (!id) return;
    const key = String(id);
    if (!map[key]) map[key] = [];
    map[key].push(normalizeDashboardResultRow(row));
  });

  return map;
}

function buildAuthHeaders(authHeader) {
  return {
    'Content-Type': 'application/json',
    Accept: '*/*',
    Authorization: authHeader,
  };
}

/**
 * Fetches assessments, enrollments, and trainings in parallel.
 */
export async function fetchDashboardCounts(apiUrl, userId, authHeader) {
  const headers = buildAuthHeaders(authHeader);

  const [assessmentsRes, studentsRes, trainingsRes] = await Promise.all([
    axios.get(`${apiUrl}/api/Assessment/${userId}`, { headers }),
    axios.get(`${apiUrl}/api/studentcourses/${userId}`, { headers }),
    axios.get(`${apiUrl}/api/trainings/${userId}`, { headers }).catch(() => ({ data: [] })),
  ]);

  const assessmentPayload = assessmentsRes.data;
  const assessmentList = parseAssessmentList(assessmentPayload);
  const assessmentCount = resolveAssessmentCount(assessmentPayload, assessmentList);

  const studentsData = studentsRes.data;
  const studentList = Array.isArray(studentsData) ? studentsData : [];

  const trainingsData = trainingsRes.data;
  const trainings = Array.isArray(trainingsData) ? trainingsData : [];

  return {
    assessmentCount,
    assessmentList,
    studentCount: studentList.length,
    studentList,
    trainings,
  };
}

/**
 * Fetches aggregated dashboard summary (counts, plan limits, trend, activity).
 */
export async function fetchDashboardSummary(apiUrl, userId, authHeader, options = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), DASHBOARD_SUMMARY_TIMEOUT_MS);

  try {
    const response = await fetch(
      buildDashboardSummaryUrl(apiUrl, userId, options),
      { headers: buildAuthHeaders(authHeader), signal: controller.signal },
    );

    if (!response.ok) {
      const error = new Error('Dashboard summary fetch failed');
      error.status = response.status;
      throw error;
    }

    return response.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * Fetches all org-wide submission rows in one request (Scores page endpoint).
 */
export async function fetchDashboardResults(apiUrl, userId, authHeader) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SCORES_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(
      buildScoresListUrl(apiUrl, userId),
      {
        headers: buildAuthHeaders(authHeader),
        signal: controller.signal,
      },
    );

    const rows = await parseScoresListResponse(response);
    return groupResultsByTestId(rows);
  } finally {
    window.clearTimeout(timeoutId);
  }
}
